import ControlPanelController from "./control_panel_controller"

export default class extends ControlPanelController {
  static targets = [
    "searchInput",
    "createModal", "editModal", "deleteConfirmModal",
    "createName", "createActionsList", "createContractorsChangeStatus",
    "editId", "editName", "editActionsList", "editContractorsChangeStatus", "editReviewStamp",
    "deleteId",
    "remindBefore", "remindAfter"
  ]

  static values = {
    createUrl: String,
    baseUrl: String,
    toggleUrl: String,
    setDefaultUrl: String,
    toggleOwnersUrl: String,
    toggleConsultantsUrl: String,
    toggleEditNumberUrl: String,
    toggleCreatorUrl: String,
    toggleOwnersDeleteUrl: String,
    toggleMultipleAssignedToUrl: String,
    toggleAssignedToOptionalUrl: String,
    togglePromptBicUrl: String,
    reminderDefaultsUrl: String
  }

  connect() {
    super.connect()
    this._createActionCounter = 0
    this._editActionCounter = 0
  }

  // ── Search ────────────────────────────────────────────────────────

  search() {
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim().toLowerCase() : ""
      const rows = this.element.querySelectorAll("tbody tr")
      rows.forEach(row => {
        const text = row.textContent.toLowerCase()
        row.style.display = !q || text.includes(q) ? "" : "none"
      })
    }, 200)
  }

  // ── Create ────────────────────────────────────────────────────────

  openCreateModal() {
    if (this.hasCreateModalTarget) {
      this._createActionCounter = 0
      this.createActionsListTarget.innerHTML = ""
      // Default starting point (Phelipe 2026-08-05): every new type opens
      // with Open + Close (Closes ✓) prefilled — rename, delete, or add
      // rows before saving. Matches the server's default for statusless
      // types, so what you see is what a bare type would get anyway.
      const defaults = [
        { name: "Open", closes_item: false },
        { name: "Close", closes_item: true }
      ]
      defaults.forEach((a) => {
        this._createActionCounter++
        this.createActionsListTarget.insertAdjacentHTML("beforeend", this._actionRowHtml(this._createActionCounter, a))
      })
      this.createModalTarget.classList.remove("hidden")
      this._lockScroll()
    }
  }

  closeCreateModal() {
    if (this.hasCreateModalTarget) {
      this.createModalTarget.classList.add("hidden")
      this._clearModalInputs(this.createModalTarget)
      this.createActionsListTarget.innerHTML = ""
      this._unlockScroll()
    }
  }

  addCreateAction() {
    this._createActionCounter++
    this.createActionsListTarget.insertAdjacentHTML("beforeend", this._actionRowHtml(this._createActionCounter))
  }

  removeAction(event) {
    const row = event.currentTarget.closest("[data-action-row]")
    const idInput = row.querySelector("[data-field='action-id']")
    if (idInput && idInput.value) {
      row.style.display = "none"
      row.dataset.destroy = "1"
    } else {
      row.remove()
    }
  }

  async submitCreate() {
    const name = this.createNameTarget.value.trim()
    if (!name) return

    const actions = this._collectActions(this.createActionsListTarget)
    const payload = {
      punchlist_kind: {
        name,
        allow_contractors_change_status: this.hasCreateContractorsChangeStatusTarget && this.createContractorsChangeStatusTarget.checked,
        punchlist_kind_actions_attributes: actions
      }
    }

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(payload)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeCreateModal()
        this._reloadWithToast(data.message || "Created successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Edit ──────────────────────────────────────────────────────────

  async openEditModal(event) {
    document.dispatchEvent(new CustomEvent("modal:open"))
    const btn = event.currentTarget
    const id = btn.dataset.id
    this.editIdTarget.value = id
    this.editNameTarget.value = btn.dataset.name || ""
    this._editActionCounter = 0
    this.editActionsListTarget.innerHTML = '<div class="text-[13px] text-gray-400">Loading...</div>'
    // Locked until the saved value loads, so a failed load can't save "off"
    // over a type that has it on.
    if (this.hasEditContractorsChangeStatusTarget) {
      this.editContractorsChangeStatusTarget.checked = false
      this.editContractorsChangeStatusTarget.disabled = true
    }
    if (this.hasEditReviewStampTarget) {
      this.editReviewStampTarget.checked = false
      this.editReviewStampTarget.disabled = true
    }

    this.editModalTarget.classList.remove("hidden")
    this._lockScroll()

    try {
      const response = await fetch(`${this.baseUrlValue}/${id}`, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      this.editActionsListTarget.innerHTML = ""
      if (this.hasEditContractorsChangeStatusTarget && response.ok) {
        this.editContractorsChangeStatusTarget.checked = !!data.allow_contractors_change_status
        this.editContractorsChangeStatusTarget.disabled = false
      }
      // The review stamp on the PDF (Phelipe 2026-10-26).
      if (this.hasEditReviewStampTarget && response.ok) {
        this.editReviewStampTarget.checked = !!data.pdf_review_stamp
        this.editReviewStampTarget.disabled = false
      }
      if (data.punchlist_kind_actions) {
        data.punchlist_kind_actions.forEach(action => {
          this._editActionCounter++
          this.editActionsListTarget.insertAdjacentHTML("beforeend",
            this._actionRowHtml(this._editActionCounter, action)
          )
        })
      }
    } catch {
      this.editActionsListTarget.innerHTML = '<div class="text-[13px] text-red-500">Failed to load actions</div>'
    }
  }

  closeEditModal() {
    if (this.hasEditModalTarget) {
      this.editModalTarget.classList.add("hidden")
      this._clearModalInputs(this.editModalTarget)
      this.editActionsListTarget.innerHTML = ""
      this._unlockScroll()
    }
  }

  addEditAction() {
    this._editActionCounter++
    this.editActionsListTarget.insertAdjacentHTML("beforeend", this._actionRowHtml(this._editActionCounter))
  }

  // removeAction handles both create and edit modes

  async submitUpdate() {
    const id = this.editIdTarget.value
    const name = this.editNameTarget.value.trim()
    if (!name) return

    const actions = this._collectActions(this.editActionsListTarget, true)
    const payload = {
      punchlist_kind: {
        name,
        punchlist_kind_actions_attributes: actions
      }
    }
    const contractors = this.hasEditContractorsChangeStatusTarget ? this.editContractorsChangeStatusTarget : null
    if (contractors && !contractors.disabled) {
      payload.punchlist_kind.allow_contractors_change_status = contractors.checked
    }
    const stamp = this.hasEditReviewStampTarget ? this.editReviewStampTarget : null
    if (stamp && !stamp.disabled) payload.punchlist_kind.pdf_review_stamp = stamp.checked

    try {
      const response = await fetch(`${this.baseUrlValue}/${id}`, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify(payload)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeEditModal()
        // Update row name and actions count
        const row = this.element.querySelector(`tbody tr[data-id="${id}"]`)
        if (row) {
          const cells = row.querySelectorAll("td")
          if (cells[0]) cells[0].textContent = data.name || name
          if (cells[1]) cells[1].textContent = `${data.actions_count || 0} actions`
          if (cells[1]) cells[1].dataset.actionsCount = data.actions_count || 0
          const editBtn = row.querySelector('[data-action*="openEditModal"]')
          if (editBtn) editBtn.dataset.name = data.name || name
        }
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Delete ────────────────────────────────────────────────────────

  openDeleteConfirm(event) {
    document.dispatchEvent(new CustomEvent("modal:open"))
    this.deleteIdTarget.value = event.currentTarget.dataset.id
    this.deleteConfirmModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeDeleteConfirm() {
    this.deleteConfirmModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  async submitDelete() {
    const id = this.deleteIdTarget.value
    try {
      const response = await fetch(`${this.baseUrlValue}/${id}`, {
        method: "DELETE",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeDeleteConfirm()
        const row = this.element.querySelector(`tbody tr[data-id="${id}"]`)
        if (row) row.remove()
        this._showToast(data.message || "Deleted successfully")
      } else {
        this.closeDeleteConfirm()
        this._showToast(data.message || "Cannot delete", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Toggle Active ─────────────────────────────────────────────────

  async toggleActive(event) {
    const id = event.currentTarget.dataset.id
    try {
      const response = await fetch(`${this.baseUrlValue}/${id}/toggle_active`, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        const row = this.element.querySelector(`tbody tr[data-id="${id}"]`)
        if (row) {
          const nameCell = row.querySelector("td:first-child")
          const badge = nameCell.querySelector('[data-badge="deactivated"]')
          const toggleBtn = row.querySelector('[data-action*="toggleActive"]')

          if (data.hidden) {
            if (!badge) {
              nameCell.insertAdjacentHTML("beforeend",
                ' <span class="ml-1 text-[13px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded" data-badge="deactivated">Deactivated</span>')
            }
            if (toggleBtn) toggleBtn.textContent = "Activate"
          } else {
            if (badge) badge.remove()
            if (toggleBtn) toggleBtn.textContent = "Deactivate"
          }
        }
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Reminder defaults (SLC3-11, SLCC 2026-09-27) ────────────────
  // The target-date email reminders new action items start with.

  async saveReminderDefaults() {
    if (!this.hasReminderDefaultsUrlValue) return
    try {
      const response = await fetch(this.reminderDefaultsUrlValue, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify({ before: this.remindBeforeTarget.value, after: this.remindAfterTarget.value })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Set Default ──────────────────────────────────────────────────

  async setDefault(event) {
    if (!this.hasSetDefaultUrlValue) return
    const id = event.currentTarget.dataset.id
    try {
      const url = `${this.setDefaultUrlValue}/${id}`
      const response = await fetch(url, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._reloadWithToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Toggle (multiple ball in court) ───────────────────────────────

  async toggle() {
    if (!this.hasToggleUrlValue) return
    try {
      const response = await fetch(this.toggleUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  async toggleOwners() {
    if (!this.hasToggleOwnersUrlValue) return
    try {
      const response = await fetch(this.toggleOwnersUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  async toggleConsultants() {
    if (!this.hasToggleConsultantsUrlValue) return
    try {
      const response = await fetch(this.toggleConsultantsUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Toggle edit number ────────────────────────────────────────────

  async toggleEditNumber() {
    if (!this.hasToggleEditNumberUrlValue) return
    try {
      const response = await fetch(this.toggleEditNumberUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  async toggleCreator() {
    if (!this.hasToggleCreatorUrlValue) return
    try {
      const response = await fetch(this.toggleCreatorUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  async toggleOwnersDelete() {
    if (!this.hasToggleOwnersDeleteUrlValue) return
    try {
      const response = await fetch(this.toggleOwnersDeleteUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  async toggleMultipleAssignedTo() {
    if (!this.hasToggleMultipleAssignedToUrlValue) return
    try {
      const response = await fetch(this.toggleMultipleAssignedToUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // "Make Assigned To optional" (Phelipe 2026-09-01).
  async toggleAssignedToOptional() {
    if (!this.hasToggleAssignedToOptionalUrlValue) return
    try {
      const response = await fetch(this.toggleAssignedToOptionalUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  async togglePromptBic() {
    if (!this.hasToggleAssignedToOptionalUrlValue) return
    try {
      const response = await fetch(this.togglePromptBicUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Action row helpers ────────────────────────────────────────────

  _actionRowHtml(counter, action = null) {
    const id = action?.id || ""
    const name = this._esc(action?.name || "")
    const closesItem = action?.closes_item || false
    const incrementsVersion = action?.increments_version || false
    const createsPcoDraft = action?.creates_pco_draft || false
    const position = action?.position ?? counter

    return `<div data-action-row="${counter}" class="flex items-center gap-2 py-1 rounded-lg"
                 data-action="dragover->cpanel-punchlist-kinds#dragOverAction drop->cpanel-punchlist-kinds#dropAction">
      <span data-drag-handle draggable="true" title="Drag to reorder"
            data-action="dragstart->cpanel-punchlist-kinds#dragStartAction dragend->cpanel-punchlist-kinds#dragEndAction"
            class="shrink-0 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 select-none px-0.5">
        <svg class="w-4 h-4 pointer-events-none" viewBox="0 0 20 20" fill="currentColor"><circle cx="7" cy="5" r="1.5"/><circle cx="13" cy="5" r="1.5"/><circle cx="7" cy="10" r="1.5"/><circle cx="13" cy="10" r="1.5"/><circle cx="7" cy="15" r="1.5"/><circle cx="13" cy="15" r="1.5"/></svg>
      </span>
      <input type="hidden" data-field="action-id" value="${id}">
      <input type="hidden" data-field="position" value="${position}">
      <input type="text" data-field="action-name" value="${name}" placeholder="Action name"
             class="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand min-w-0">
      <label class="inline-flex items-center gap-1 whitespace-nowrap cursor-pointer" title="Closes/resolves the item">
        <input type="checkbox" data-field="closes-item" ${closesItem ? "checked" : ""}
               class="rounded border-gray-300 text-red-500 focus:ring-red-500 cursor-pointer"
               data-action="change->cpanel-punchlist-kinds#toggleClosesItem">
        <span class="text-[13px] ${closesItem ? "text-red-600 font-medium" : "text-gray-500"}">Closes</span>
      </label>
      <label class="inline-flex items-center gap-1 whitespace-nowrap cursor-pointer ${closesItem ? "hidden" : ""}" data-field="version-label" title="Increments version (e.g. v1 to v2)">
        <input type="checkbox" data-field="increments-version" ${incrementsVersion ? "checked" : ""}
               class="rounded border-gray-300 text-brand focus:ring-brand cursor-pointer">
        <span class="text-[13px] text-gray-500">Version++</span>
      </label>
      <label class="inline-flex items-center gap-1 whitespace-nowrap cursor-pointer" title="Moving an item into this status creates a PCO draft under Finances">
        <input type="checkbox" data-field="creates-pco-draft" ${createsPcoDraft ? "checked" : ""}
               class="rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer">
        <span class="text-[13px] text-gray-500">PCO Draft</span>
      </label>
      <button type="button" class="p-1 text-gray-400 hover:text-red-500 cursor-pointer shrink-0"
              data-action="click->cpanel-punchlist-kinds#removeAction">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>
      </button>
    </div>`
  }

  // Drag to reorder the action rows (Phelipe 2026-08-28). The save already
  // derives `position` from DOM order, so moving the row IS the reorder —
  // nothing extra to persist. Only the grip starts a drag, so the name
  // inputs stay selectable.
  dragStartAction(event) {
    const row = event.currentTarget.closest("[data-action-row]")
    if (!row) return

    this._dragRow = row
    event.dataTransfer.effectAllowed = "move"
    // Firefox won't start a drag without data set.
    try { event.dataTransfer.setData("text/plain", row.dataset.actionRow) } catch (_) {}
    // Deferred so the drag image is the row as it looks now, not faded.
    setTimeout(() => row.classList.add("opacity-40"), 0)
  }

  // Live reorder: the dragged row slides through its siblings so the list
  // always shows the order you'd get on release.
  dragOverAction(event) {
    if (!this._dragRow) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const over = event.currentTarget
    if (over === this._dragRow) return

    const list = this._dragRow.parentElement
    if (!list || over.parentElement !== list) return

    const box = over.getBoundingClientRect()
    const after = event.clientY > box.top + box.height / 2
    list.insertBefore(this._dragRow, after ? over.nextSibling : over)
  }

  dropAction(event) {
    if (this._dragRow) event.preventDefault()
  }

  dragEndAction() {
    this._dragRow?.classList.remove("opacity-40")
    this._dragRow = null
  }

  toggleClosesItem(event) {
    const row = event.currentTarget.closest("[data-action-row]")
    const closesChecked = event.currentTarget.checked
    const closesLabel = row.querySelector("[data-field='closes-item']").closest("label").querySelector("span")
    const versionLabel = row.querySelector("[data-field='version-label']")

    if (closesChecked) {
      closesLabel.className = "text-[13px] text-red-600 font-medium"
      versionLabel.classList.add("hidden")
      // Uncheck increments-version when closes is on
      const versionCheckbox = row.querySelector("[data-field='increments-version']")
      if (versionCheckbox) versionCheckbox.checked = false
    } else {
      closesLabel.className = "text-[13px] text-gray-500"
      versionLabel.classList.remove("hidden")
    }
  }

  _collectActions(container, includeDestroy = false) {
    const actions = []
    container.querySelectorAll("[data-action-row]").forEach((row, index) => {
      const idInput = row.querySelector("[data-field='action-id']")
      const nameInput = row.querySelector("[data-field='action-name']")
      const closesInput = row.querySelector("[data-field='closes-item']")
      const versionInput = row.querySelector("[data-field='increments-version']")
      const pcoInput = row.querySelector("[data-field='creates-pco-draft']")

      const actionData = {
        name: nameInput?.value?.trim() || "",
        closes_item: closesInput?.checked || false,
        increments_version: versionInput?.checked || false,
        creates_pco_draft: pcoInput?.checked || false,
        position: index
      }

      if (idInput?.value) actionData.id = idInput.value

      if (includeDestroy && row.dataset.destroy === "1") {
        actionData._destroy = true
      }

      if (actionData.name || actionData._destroy) {
        actions.push(actionData)
      }
    })
    return actions
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;")
  }
}
